import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { AssetManagement } from '@aptsi-types';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { SystemAssetSourceService } from '../services/system-asset-source.service'
import { WorkflowNavigationService } from '../../shared/workflow-navigation.service';
@Component({
  selector: 'app-system-asset-manual-create-or-update',
  templateUrl: './system-asset-manual-create-or-update.component.html',
  styleUrls: ['./system-asset-manual-create-or-update.component.scss']
})
export class SystemAssetManualCreateOrUpdateComponent implements OnInit {
  form!: FormGroup;
  isCreate = true;
  systemAssetId: string | null = null;
  assetSourceId: string | null = null;

  loading = false;
  saving = false;
  completionMessage = '';
  pageError = '';

  importedBy = '';
  importedByKeycloakId = '';

  readonly statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' }
  ];

  readonly lifecycleOptions = [
    { value: 'Development', label: 'Development' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Staging', label: 'Staging' },
    { value: 'Production', label: 'Production' },
    { value: 'Retired', label: 'Retired' }
  ];

  readonly classificationOptions = [
    { value: 'PUBLIC', label: 'Public' },
    { value: 'INTERNAL', label: 'Internal' },
    { value: 'CONFIDENTIAL', label: 'Confidential' },
    { value: 'RESTRICTED', label: 'Restricted' }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly systemAssetSourceService: SystemAssetSourceService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly dialog: MatDialog,
    private readonly workflowNavigation: WorkflowNavigationService
  ) {}

  ngOnInit(): void {
    this.systemAssetId = this.readParam('systemAssetId') || this.readParam('systemassetid');
    this.assetSourceId =
      this.readParam('assetSourceId') ||
      this.readParam('assetsourceId') ||
      this.route.snapshot.queryParamMap.get('assetSourceId') ||
      this.route.snapshot.queryParamMap.get('assetsourceId');

    const createFlag = this.route.snapshot.data['create'];
    this.isCreate = createFlag !== undefined ? Boolean(createFlag) : !this.systemAssetId;

    this.importedBy = this.getImportedByFromKeycloak();
    this.importedByKeycloakId = this.getKeycloakUserId();
    this.buildForm();

    if (!this.isCreate) {
      if (!this.systemAssetId) {
        this.openErrorDialog('System Asset ID is missing.');
        return;
      }
      this.loadManualSystemAsset(this.systemAssetId);
    }
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add System Asset' : 'Update System Asset';
  }

  get pageDescription(): string {
    return this.isCreate
      ? 'Add a system asset manually and associate it with an existing asset source.'
      : 'Review and update the details for this system asset.';
  }

  get controls(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  private buildForm(): void {
    this.form = this.fb.group({
      systemAssetName: ['', [Validators.required, Validators.maxLength(120)]],
      systemAssetDescription: ['', [Validators.required, Validators.maxLength(1000)]],
      systemAssetOwner: ['', [Validators.required, Validators.maxLength(120)]],
      systemAssetStatus: ['active', Validators.required],
      systemAssetEndpoint: ['', [Validators.maxLength(2048), Validators.pattern(/^$|^https?:\/\/.+/i)]],
      systemAssetLifecycleStatus: ['Production', Validators.required],
      systemAssetBusinessValue: [5, [Validators.required, Validators.min(1), Validators.max(10)]],
      dataClassification: ['CONFIDENTIAL', Validators.required]
    });
  }

  private readParam(key: string): string | null {
    // Handles IDs supplied on this route or a parent route.
    let current: ActivatedRoute | null = this.route;
    while (current) {
      const value = current.snapshot.paramMap.get(key);
      if (value) {
        return value;
      }
      current = current.parent;
    }
    return null;
  }

  private getImportedByFromKeycloak(): string {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      const userLabel = token['preferred_username'] || token['name'] || token['email'];
      return userLabel ? String(userLabel) : '';
    } catch {
      return '';
    }
  }

  private getKeycloakUserId(): string {
    try {
      const stateUserId = this.keycloakStateService.getUserId();
      if (stateUserId) {
        return String(stateUserId);
      }

      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      return token['sub'] ? String(token['sub']) : '';
    } catch {
      return '';
    }
  }

  loadManualSystemAsset(systemAssetId: string): void {
    this.loading = true;
    this.pageError = '';

    this.systemAssetSourceService.getManualSystemAsset(systemAssetId).subscribe({
      next: (response: AssetManagement) => {
        this.loading = false;
        if (!response) {
          this.openErrorDialog('No details were found for this system asset.');
          return;
        }

        const asset: any = response;
        this.form.patchValue({
          systemAssetName: asset.systemAssetName ?? '',
          systemAssetDescription: asset.systemAssetDescription ?? '',
          systemAssetOwner: asset.systemAssetOwner ?? '',
          systemAssetStatus: String(asset.systemAssetStatus ?? 'active').toLowerCase(),
          systemAssetEndpoint: asset.systemAssetEndpoint ?? '',
          systemAssetLifecycleStatus: asset.systemAssetLifecycleStatus ?? 'Production',
          systemAssetBusinessValue: asset.systemAssetBusinessValue ?? 5,
          dataClassification: String(asset.dataClassification ?? 'CONFIDENTIAL').toUpperCase()
        });
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(this.extractMessage(error) || 'Unable to load the system asset details.');
      }
    });
  }

  save(): void {
    if (this.saving || this.loading) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.pageError = 'Review the highlighted fields and correct the validation errors.';
      return;
    }

    if (!this.importedByKeycloakId) {
      this.pageError = 'Unable to identify the signed-in user. Please sign in again and retry.';
      return;
    }

    this.pageError = '';
    this.saving = true;
    const value = this.form.getRawValue();
    const businessValue = String(value.systemAssetBusinessValue);

    if (this.isCreate) {
      this.systemAssetSourceService.createManualSystemAsset(
        String(value.systemAssetName).trim(),
        String(value.systemAssetDescription).trim(),
        String(value.systemAssetOwner).trim(),
        String(value.systemAssetStatus),
        String(value.systemAssetEndpoint ?? '').trim(),
        String(value.systemAssetLifecycleStatus),
        businessValue,
        String(value.dataClassification),
        this.importedBy,
        this.importedByKeycloakId
      ).subscribe({
        next: response => this.onSaveSuccess(response, true),
        error: error => this.onSaveError(error, 'Unable to create the system asset.')
      });
      return;
    }

    if (!this.systemAssetId) {
      this.saving = false;
      this.pageError = 'System Asset ID is missing. Unable to update this asset.';
      return;
    }

    this.systemAssetSourceService.updateManualSystemAsset(
      this.systemAssetId,
      String(value.systemAssetDescription).trim(),
      String(value.systemAssetOwner).trim(),
      String(value.systemAssetStatus),
      String(value.systemAssetEndpoint ?? '').trim(),
      String(value.systemAssetLifecycleStatus),
      businessValue,
      String(value.dataClassification),
      this.importedBy,
      this.importedByKeycloakId
    ).subscribe({
      next: response => this.onSaveSuccess(response, false),
      error: error => this.onSaveError(error, 'Unable to update the system asset.')
    });
  }

  private onSaveSuccess(response: AssetManagement, isCreate: boolean): void {
    this.saving = false;
    const responseMessage = this.extractMessage(response);
    this.completionMessage = responseMessage ||
      (isCreate ? 'System asset created successfully.' : 'System asset updated successfully.');

    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '380px',
      data: {
        isCreate,
        isCreateUpdate: true,
        title: 'System Asset',
        message: this.completionMessage
      }
    });

    dialogRef.afterClosed().subscribe(() => this.navigateBack());
  }

  private onSaveError(error: unknown, fallbackMessage: string): void {
    this.saving = false;
    this.openErrorDialog(this.extractMessage(error) || fallbackMessage);
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
            void this.workflowNavigation.navigateToWorkflow(
    'systemAssetCapture'
  );
  }

  navigateToSystemAssetCapture(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'systemAssetCapture'
  );
}

  private openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '380px',
      data: { errorMessage }
    });
  }

  private extractMessage(responseOrError: any): string {
    if (!responseOrError) {
      return '';
    }

    if (typeof responseOrError === 'string') {
      return responseOrError;
    }

    const candidates = [
      responseOrError.message,
      responseOrError.error?.message,
      responseOrError.error?.errorMessage,
      responseOrError.error?.details,
      responseOrError.errorMessage
    ];

    const message = candidates.find(candidate => typeof candidate === 'string' && candidate.trim());
    return message ? String(message).trim() : '';
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }
}
